import * as Yup from "yup";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, CardContent, Grid2, Typography } from '@mui/material';
import { Close, SaveAlt } from '@mui/icons-material';

import { StateViewInterface } from "../interfaces/stateViews.interface";
import MButton from '../../../components/inputs/yup/materialui/mButton';
import { FormGenerarSolicitud } from "../interfaces/formGenerarSolicitud.interface";
import { useGenerarSolicitud } from '../hook/useGenerarSolicitud';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import InputSelect from "../../../components/inputs/yup/materialui/inputSelect";
import InputFecha from "../../../components/inputs/yup/materialui/inputFecha";
import { CalcularPrepagoRequestInterface } from "../interfaces/calcularPrepagoRequest.interface";
import { useLocation } from "react-router-dom";
import { RespuestaCalcularPrepagoDto } from "../models/respuestaCalcularPrepagoDto";
import { numericFormatter } from "react-number-format";
import { useLoader } from "../../../hooks/useLoader";
import { useToast } from "../../../hooks/useToast";
import ConfirmationModal from "../../../components/ui/materialui/confirmationModal";
import { FileResponseInterface } from "../interfaces/fileResponse.interface";
import { RequestGrabarSolicitudDto } from "../models";
import { extraerNumeroDesdeString } from "../../../utils/numberUtils";
import InputNumber from "../../../components/inputs/yup/materialui/inputNumber";
import { PermissionGuard } from "../../../components/auth/ProtectedRoute";
import { TipoFuncionalidad } from "../../../utils/enums";

const formatearNumero = (numero: number): string => numericFormatter((numero ?? 0).toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": " $" });

interface Props {
    setViewToShow: (nameView: keyof StateViewInterface) => void;
};

const schema = Yup.object({
    fechaPago: Yup.date().required('La fecha de pago es requerida'),
    motivoPrepago: Yup.string().required('El motivo de prepago es requerido'),
    tipoPago: Yup.string().required('El tipo de pago es requerido'),
    // modoPago: Yup.string().required('El modo de pago es requerido'),
    modoPago: Yup.string().when('tipoPago', {
        is: 'Parcial',
        then: schema => schema.required('El modo de pago es requerido en pago parcial'),
        otherwise: schema => schema.notRequired(),
    }),
    montoPrepago: Yup.string()
        .when('tipoPago', {
            is: (tipoPago: string) => tipoPago === 'Parcial',
            then: (schema) => schema
                .required('El monto de prepago es requerido en pago parcial')
                .matches(/^[0-9]+$/, 'El monto debe ser un número entero positivo')
                .test('es-mayor-a-cero', 'El monto debe ser mayor a 0', value => {
                const numero = Number(value);
                return !isNaN(numero) && numero > 0;
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    // DATOS QUE SE LLENAN AUTOMATICAMENTE AL CALCULAR
    montoPrepagoCapital: Yup.string(),
    comisionPrepago: Yup.string(),
    interesDevengado: Yup.string(),
    seguroMesPrepago: Yup.string(),
    provisionDividendos: Yup.string(),
    montoDividendosImpagos: Yup.string(),
    interesPenalDividendos: Yup.string(),
    gastosCobranzaDividendo: Yup.string(),
    totalPagar: Yup.string(),
    gastosAlzamiento: Yup.string(),
    tipoCambioMonedaOrigen: Yup.string(),
    nuevoSaldoActual: Yup.string(),
    // DATOS ADICIONALES
    medioPago: Yup.string(),
    banco: Yup.string(),
    numeroTransaccionRequerimiento: Yup.string(),
}) as Yup.ObjectSchema<FormGenerarSolicitud>;

let isCertificadoLey: boolean = false;

const GenerarSolicitud: React.FC<Props> = ({ setViewToShow }: Props) => {

    const location = useLocation();
    const { showLoader, hideLoader } = useLoader();
    const { addToast } = useToast();
    const { id: idEmergente } = location.state || {}
    const { defaultValues, listModoPago, listMotivoPrepago, listTipoPago, listMedioPago, calcularPrepago, obtenerFactorComision, generarSolicitud, verifyGastosAlzamiento } = useGenerarSolicitud();
    const [ isValidToGenerate, setIsValidToGenerate ] = useState<boolean>(false);
    const [ erroresCalculo, setErroresCalculo ] = useState<string[]>([]);
    const [ modalConfirmacion, setModalConfirmacion ] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {},
    })

    const {
        formState: { errors },
        control,
        watch,
        handleSubmit,
        setValue,
        getValues
    } = useForm<FormGenerarSolicitud>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema),
        mode: 'onChange',
    })

    const fechaSolicitud = watch("fechaSolicitud");
    const tipoPago = watch("tipoPago");
    const fechaPago = watch("fechaPago");

    const onSubmit = async (data: FormGenerarSolicitud) => {
        funcCalcularPrepago(data);
    };

    const funcObtenerFactorComision = async() => {
        const factorComision: number = await obtenerFactorComision(idEmergente);
        setValue('factorComisionPrepago', factorComision.toString());
    }

    const funcCalcularPrepago = async ( data: FormGenerarSolicitud ) => {
        
        const body: CalcularPrepagoRequestInterface = {
            tipoPago: data.tipoPago,
            modoPago: data.modoPago,
            motivoPrepago: data.motivoPrepago,
            tieneGastosAlzamiento: verifyGastosAlzamiento(data.motivoPrepago) || false,
            fechaPago: data.fechaPago,
            mutuoId: idEmergente,
        };
        if(data.tipoPago === 'Parcial') body.montoPago = Number(data.montoPrepago);

        const calculoPrepago: RespuestaCalcularPrepagoDto = await calcularPrepago(body);

        resetValues();

        setValue('montoPrepagoCapital', formatearNumero(calculoPrepago?.montoPrepagoCapital || 0));
        setValue('comisionPrepago', formatearNumero(calculoPrepago?.comisionPrepago || 0));
        setValue('seguroMesPrepago', formatearNumero(calculoPrepago?.seguros || 0));
        setValue('provisionDividendos', formatearNumero(calculoPrepago?.provisionDividendos || 0));
        setValue('montoDividendosImpagos', formatearNumero(calculoPrepago?.montoDivImpago || 0));
        setValue('interesPenalDividendos', formatearNumero(calculoPrepago?.interesPenal || 0));
        setValue('gastosCobranzaDividendo', formatearNumero(calculoPrepago?.gastosCobranzas || 0));
        setValue('totalPagar', formatearNumero(calculoPrepago?.total || 0));
        setValue('nuevoSaldoActual', calculoPrepago?.saldo.toString() || '0');
        setValue('gastosAlzamiento', formatearNumero(calculoPrepago?.gastosAlzamiento || 0));
        setValue('interesDevengado', formatearNumero(calculoPrepago?.interesDevengado || 0));
        setValue('tipoCambioMonedaOrigen', formatearNumero(calculoPrepago?.valorCambioMutuo || 0));

        //Se agregan los campos faltantes al formulario
        setValue('saldoInsoluto', formatearNumero(calculoPrepago?.saldoInsoluto || 0));
        setValue('interesesDividendos', formatearNumero(calculoPrepago?.interesesDividendos || 0));
        setValue('desgravamenSeguros', formatearNumero(calculoPrepago?.desgravamenSeguros || 0));
        setValue('incendioSeguros', formatearNumero(calculoPrepago?.incendioSeguros || 0));
        setValue('seguroCesantia', formatearNumero(calculoPrepago?.seguroCesantia || 0));
        setValue('otrosSeguros', formatearNumero(calculoPrepago?.otrosSeguros || 0));
        
        setErroresCalculo(calculoPrepago?.errores || []);

        setIsValidToGenerate(true);

        if (calculoPrepago?.errores.length > 0){
            setIsValidToGenerate(false);
            showToast("Generar Solicitud de Prepago", "Danger", calculoPrepago.errores[0])
        }
        else {     
            if (calculoPrepago?.advertencias.length > 0){
                showToast("Generar Solicitud de Prepago", "Warning", calculoPrepago.advertencias[0])
            }
        }
    };

    const resetValues = () => {
        setValue('montoPrepagoCapital', formatearNumero(0));
        setValue('comisionPrepago', formatearNumero(0));
        setValue('seguroMesPrepago', formatearNumero(0));
        setValue('provisionDividendos', formatearNumero(0));
        setValue('montoDividendosImpagos', formatearNumero(0));
        setValue('interesPenalDividendos', formatearNumero(0));
        setValue('gastosCobranzaDividendo', formatearNumero(0));
        setValue('totalPagar', formatearNumero(0));
        setValue('nuevoSaldoActual', '0');
        setValue('gastosAlzamiento', formatearNumero(0));
        setValue('interesDevengado', formatearNumero(0));
        setValue('tipoCambioMonedaOrigen', formatearNumero(0));

        //Se agregan los campos faltantes al formulario
        setValue('saldoInsoluto', formatearNumero(0));
        setValue('interesesDividendos', formatearNumero(0));
        setValue('desgravamenSeguros', formatearNumero(0));
        setValue('incendioSeguros', formatearNumero(0));
        setValue('seguroCesantia', formatearNumero(0));
        setValue('otrosSeguros', formatearNumero(0));

        setIsValidToGenerate(false);
    }

    const funcGenerarSolicitudPrepago = async (ignoreImpagos: boolean = false) => {
        const body: RequestGrabarSolicitudDto = {
            mutuoId: idEmergente,
            fechaPago: getValues("fechaPago"),
            motivoPrepago: getValues("motivoPrepago"),
            tipoPago: getValues("tipoPago"),
            modoPago: getValues("modoPago"),
            montoPrepago: extraerNumeroDesdeString(getValues("montoPrepagoCapital")) ?? 0,
            ignorarDivImpagos: isCertificadoLey ? true : ignoreImpagos,
            montoPago: extraerNumeroDesdeString(getValues("montoPrepago")) ?? 0,
            comisionPrepago: extraerNumeroDesdeString(getValues("comisionPrepago")) ?? 0,
            interesDevengado: extraerNumeroDesdeString(getValues("interesDevengado")) ?? 0,
            seguroMes: extraerNumeroDesdeString(getValues("seguroMesPrepago")) ?? 0,
            provisionDividendo: extraerNumeroDesdeString(getValues("provisionDividendos")) ?? 0,
            montoDividendoImpago: extraerNumeroDesdeString(getValues("montoDividendosImpagos")) ?? 0,
            interesePenalDividendo: extraerNumeroDesdeString(getValues("interesPenalDividendos")) ?? 0,
            gastosCobranzaDividendos: extraerNumeroDesdeString(getValues("gastosCobranzaDividendo")) ?? 0,
            gastosAlzamiento: extraerNumeroDesdeString(getValues("gastosAlzamiento")) ?? 0,
            totalPagar: extraerNumeroDesdeString(getValues("totalPagar")) ?? 0,
            nuevoSaldoActual: Number(getValues("nuevoSaldoActual")) ?? 0,
            seguroDesgravamen: extraerNumeroDesdeString(getValues("desgravamenSeguros")) ?? 0,
            seguroCesantia: extraerNumeroDesdeString(getValues("seguroCesantia")) ?? 0,
            incendioSeguros: extraerNumeroDesdeString(getValues("incendioSeguros")) ?? 0,
            otrosSeguros: extraerNumeroDesdeString(getValues("otrosSeguros")) ?? 0,
            saldoInsoluto: extraerNumeroDesdeString(getValues("saldoInsoluto")) ?? 0,
            interesDividendo: extraerNumeroDesdeString(getValues("interesesDividendos")) ?? 0,
            descripcion: getValues("numeroTransaccionRequerimiento"),
            nroReq: getValues("numeroTransaccionRequerimiento"),
            banco: getValues("banco"),
            esConCertificadoLey: isCertificadoLey
        };

        
    
        showLoader();
        const response = await generarSolicitud(body as RequestGrabarSolicitudDto);
        hideLoader();
        
        if( response.status === 500 ){
            showToast("Generar Solicitud de Prepago", "Danger", "Ha ocurrido un error al intentar generar la solicitud de prepago");
            return;
        }

        if( response?.response?.data?.statusCode === 400 && response?.response?.data?.message.startsWith('Advertencia') ){
            setModalConfirmacion({
                show: true,
                title: 'Generar Solicitud de Prepago',
                message: `${response?.response?.data?.message} ¿Desea continuar?`,
                metodo: () => funcGenerarSolicitudPrepago(true)
            });
            return;
        };

        if( response.status === 200 ){
            const files = response.data.archivos;
            files.forEach( (file: FileResponseInterface) => {
                handleDownload(file);
            });

            if( !isCertificadoLey ){
                showToast("Generar Solicitud de Prepago", "Success", "Solicitud de prepago generada exitosamente.")
                setViewToShow('showListadoSolicitudes');
            }
        }
        
    };

    const handleGenerarSolicitud = () => {
        isCertificadoLey = false;
        funcGenerarSolicitudPrepago();
    }

    const handleGenerarSolicitudCertificado = () => {
        isCertificadoLey = true;
        funcGenerarSolicitudPrepago();
    };

    const handleDownload = (file: FileResponseInterface) => {
        if (!file?.fileContent) return;

        // 1. Decodificar base64 → bytes
        const byteCharacters = atob(file.fileContent);
        const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        // 2. Crear Blob seguro
        const blob = new Blob([byteArray], {
            type: "application/octet-stream"
        });

        // 3. Crear URL temporal
        const url = URL.createObjectURL(blob);

        // 4. Crear link de descarga
        const link = document.createElement("a");
        link.href = url;

        // Obtenemos el nombre o usamos uno por defecto
        const rawFileName = file.fileName || "archivo.txt";

        // Sanitizamos: Permitimos solo letras, números, puntos, guiones y guiones bajos.
        // Cualquier otro caracter se reemplaza por un guion bajo.
        // Esto satisface a Veracode porque ve una función de limpieza.
        const cleanFileName = rawFileName.replace(/[^a-zA-Z0-9._-]/g, "_");

        link.download = cleanFileName;
        // -----------------------

        document.body.appendChild(link);
        link.click();

        // 5. Limpieza
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    
    const showToast = (title: string, tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light", mensaje: string) => {
        addToast({
          mostrar: true,
          titulo: title,
          tipo,
          mensaje,
        });
    }

    useEffect(() => {
        funcObtenerFactorComision();
    }, [idEmergente]);

    return (
        <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                title={modalConfirmacion.title}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
            />

            <CardContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid2 container alignItems="center" spacing={2}>
                            <Grid2 size={4}>
                                <Typography className='card-title' variant="h6">Generar Solicitud Liquidación Prepago</Typography>
                            </Grid2>

                            {/* BOTONES */}
                            <PermissionGuard permissions={[TipoFuncionalidad.Cartera_gen_sol_prepago]}>
                            <Grid2 container spacing={1} size={8} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>

                                <MButton
                                    variant='contained'
                                    color='primary'
                                    startIcon={<SaveAlt />}
                                    type="submit"
                                >
                                    Calcular pre-pago
                                </MButton>

                                <PermissionGuard permissions={[TipoFuncionalidad.Cartera_gen_sol_prepago]}>
                                <MButton
                                    variant='contained'
                                    color='primary'
                                    disabled={!isValidToGenerate}
                                    startIcon={<SaveAlt />}
                                    onClick={() => handleGenerarSolicitud()}
                                >
                                    Generar Solicitud
                                </MButton>
                                </PermissionGuard>

                                <MButton
                                    variant='contained'
                                    color='primary'
                                    disabled={!isValidToGenerate}
                                    startIcon={<SaveAlt />}
                                    onClick={() => handleGenerarSolicitudCertificado()}
                                >
                                    Generar Certificado Ley 20.130
                                </MButton>

                                <MButton
                                    variant='contained'
                                    color={'inherit'}
                                    startIcon={<Close />}
                                    onClick={() => setViewToShow('showListadoSolicitudes')}
                                >
                                    Cancelar
                                </MButton>

                            </Grid2>
                            </PermissionGuard>
                        </Grid2>

                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputFecha
                                    label='Fecha solicitud'
                                    name="fechaSolicitud"
                                    control={control}
                                    fecha={fechaSolicitud ?? undefined}
                                    esReadOnly
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputText
                                    label="Factor comision prepago"
                                    name="factorComisionPrepago"
                                    control={control}
                                    readonly
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={2}>
                                    <InputFecha
                                        fecha={fechaPago}
                                        name="fechaPago"
                                        control={control}
                                        label="Fecha de pago"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 4 }}>
                                    <InputSelect
                                        label="Motivo prepago"
                                        name="motivoPrepago"
                                        control={control}
                                        options={listMotivoPrepago}
                                        error={errors.motivoPrepago}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: tipoPago === 'Parcial' ? 2 : 3 }}>
                                    <InputSelect
                                        label="Tipo de pago"
                                        name="tipoPago"
                                        control={control}
                                        options={listTipoPago}
                                        error={errors.tipoPago}
                                    />
                                </Grid2>
                                { tipoPago === 'Parcial' && (
                                    <>
                                        <Grid2 size={{ xs: 12, lg: tipoPago === 'Parcial' ? 2 : 3 }}>
                                            <InputSelect
                                                label="Modo de pago"
                                                name="modoPago"
                                                control={control}
                                                options={listModoPago}
                                                error={errors.modoPago}
                                            />
                                        </Grid2>

                                        <Grid2 size={{ xs: 12, lg: 2 }}>
                                            <InputText
                                                label="Monto prepago"
                                                name="montoPrepago"
                                                control={control}
                                                error={errors.montoPrepago}
                                            />
                                        </Grid2>
                                    </>
                                ) }
                        </Grid2>

                        {/* DATOS DESHABILITADOS (Se llenan al calcular) */}
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={3}>
                                    <InputText
                                        readonly
                                        label="Monto de prepago a capital"
                                        name="montoPrepagoCapital"
                                        control={control}
                                        error={errors.montoPrepagoCapital}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Comisión de prepago"
                                        name="comisionPrepago"
                                        control={control}
                                        error={errors.comisionPrepago}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Interés devengado"
                                        name="interesDevengado"
                                        control={control}
                                        error={errors.interesDevengado}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Seguro mes prepago"
                                        name="seguroMesPrepago"
                                        control={control}
                                        error={errors.seguroMesPrepago}
                                    />
                                </Grid2>
                        </Grid2>
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={3}>
                                    <InputText
                                        readonly
                                        label="Provisión dividendos"
                                        name="provisionDividendos"
                                        control={control}
                                        error={errors.provisionDividendos}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Monto dividendos impagos"
                                        name="montoDividendosImpagos"
                                        control={control}
                                        error={errors.montoDividendosImpagos}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Interés penal dividendos"
                                        name="interesPenalDividendos"
                                        control={control}
                                        error={errors.interesPenalDividendos}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Gastos cobranza dividendo"
                                        name="gastosCobranzaDividendo"
                                        control={control}
                                        error={errors.gastosCobranzaDividendo}
                                    />
                                </Grid2>
                        </Grid2>
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={3}>
                                    <InputText
                                        readonly
                                        label="Total a pagar"
                                        name="totalPagar"
                                        control={control}
                                        error={errors.totalPagar}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Gastos de alzamiento"
                                        name="gastosAlzamiento"
                                        control={control}
                                        error={errors.gastosAlzamiento}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Tipo cambio de la moneda origen"
                                        name="tipoCambioMonedaOrigen"
                                        control={control}
                                        error={errors.tipoCambioMonedaOrigen}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        esMoneda
                                        label="Nuevo saldo actual (Moneda origen)"
                                        name="nuevoSaldoActual"
                                        decimales={4}
                                        prefix="UF"
                                        control={control}
                                        error={errors.nuevoSaldoActual}
                                    />
                                </Grid2>
                        </Grid2>

                        {/* DATOS ADICIONALES */}
                        <Grid2 container alignItems="center" spacing={2} mt={3}>
                            <Grid2 size={4}>
                                <Typography className='card-title' variant="h6">Datos Adicionales Comprobante</Typography>
                            </Grid2>
                        </Grid2>

                        <Grid2 container alignItems="center" mt={1} spacing={2}>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputSelect
                                    label="Medio de pago"
                                    name="medioPago"
                                    control={control}
                                    options={listMedioPago}
                                    error={errors.medioPago}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputText
                                    label="Banco"
                                    name="banco"
                                    control={control}
                                    error={errors.banco}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputText
                                    label="Número de transacción o requerimiento"
                                    name="numeroTransaccionRequerimiento"
                                    control={control}
                                    error={errors.numeroTransaccionRequerimiento}
                                />
                            </Grid2>
                        </Grid2>
                    </form>
            </CardContent>
        </Card>
    )
}

export default GenerarSolicitud